import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { CALENDAR_MODE, businessDate, pickerDate, calendarWeekCount } from './okrCycleHelpers';

const EMPTY_FORM = {
    name: '',
    startDate: null,
    endDate: null,
    numberOfWeeks: 13,
    pointsBase: 1000,
    status: 'draft'
};

const STATUS_OPTIONS = [
    { label: 'Borrador', value: 'draft' },
    { label: 'Activo', value: 'active' },
    { label: 'Cerrado', value: 'closed' }
];

const toDateValue = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const buildInitialForm = (cycle) => {
    if (!cycle) return EMPTY_FORM;

    return {
        name: cycle.name || '',
        startDate: toDateValue(cycle.calendarMode === CALENDAR_MODE ? `${businessDate(cycle.startDate)}T12:00:00` : cycle.startDate),
        endDate: toDateValue(cycle.calendarMode === CALENDAR_MODE ? `${businessDate(cycle.endDate)}T12:00:00` : cycle.endDate),
        numberOfWeeks: Number(cycle.numberOfWeeks || 13),
        pointsBase: Number(cycle.pointsBase || 1000),
        status: cycle.status || 'draft'
    };
};

const OkrCycleDialog = ({ visible, cycle, saving, onHide, onSubmit }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const isEditMode = Boolean(cycle?._id);
    const useCalendar = !isEditMode || cycle?.calendarMode === CALENDAR_MODE;
    const numberOfWeeks = useCalendar ? calendarWeekCount(pickerDate(form.startDate), pickerDate(form.endDate)) : Number(form.numberOfWeeks);

    useEffect(() => {
        if (visible) {
            setForm(buildInitialForm(cycle));
            setErrors({});
        }
    }, [cycle, visible]);

    const title = useMemo(() => (isEditMode ? 'Editar Ciclo' : 'Nuevo Ciclo'), [isEditMode]);

    const updateField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: null }));
    };

    const validate = () => {
        const nextErrors = {};
        const start = form.startDate ? new Date(form.startDate).getTime() : null;
        const end = form.endDate ? new Date(form.endDate).getTime() : null;

        if (!form.name?.trim()) nextErrors.name = 'El nombre del ciclo es obligatorio.';
        if (!form.startDate) nextErrors.startDate = 'La fecha de inicio es obligatoria.';
        if (!form.endDate) nextErrors.endDate = 'La fecha de cierre es obligatoria.';
        if (start && end && end <= start) nextErrors.endDate = 'La fecha de cierre debe ser posterior a la fecha de inicio.';
        if (!numberOfWeeks || numberOfWeeks <= 0) nextErrors.numberOfWeeks = 'El número de semanas debe ser mayor que 0.';
        if (!form.pointsBase || Number(form.pointsBase) <= 0) nextErrors.pointsBase = 'Los puntos base deben ser mayores que 0.';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        onSubmit({
            name: form.name.trim(),
            startDate: useCalendar ? pickerDate(form.startDate) : form.startDate,
            endDate: useCalendar ? pickerDate(form.endDate) : form.endDate,
            numberOfWeeks,
            ...(useCalendar ? { calendarMode: CALENDAR_MODE } : {}),
            pointsBase: Number(form.pointsBase),
            status: form.status
        });
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={saving} />
            <Button label={isEditMode ? 'Guardar cambios' : 'Crear ciclo'} icon="pi pi-check" onClick={handleSubmit} loading={saving} />
        </div>
    );

    return (
        <Dialog header={title} visible={visible} onHide={onHide} footer={footer} className="w-full md:w-30rem" modal draggable={false}>
            <div className="formgrid grid">
                <div className="field col-12">
                    <label htmlFor="okr-cycle-name" className="font-medium">Nombre del ciclo</label>
                    <InputText
                        id="okr-cycle-name"
                        value={form.name}
                        onChange={(event) => updateField('name', event.target.value)}
                        placeholder="Q4 2026"
                        className={`w-full mt-2 ${errors.name ? 'p-invalid' : ''}`}
                    />
                    {errors.name ? <small className="p-error">{errors.name}</small> : null}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="okr-cycle-start" className="font-medium">Fecha de inicio</label>
                    <Calendar
                        id="okr-cycle-start"
                        value={form.startDate}
                        onChange={(event) => updateField('startDate', event.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        className={`w-full mt-2 ${errors.startDate ? 'p-invalid' : ''}`}
                    />
                    {errors.startDate ? <small className="p-error">{errors.startDate}</small> : null}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="okr-cycle-end" className="font-medium">Fecha de cierre</label>
                    <Calendar
                        id="okr-cycle-end"
                        value={form.endDate}
                        onChange={(event) => updateField('endDate', event.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        className={`w-full mt-2 ${errors.endDate ? 'p-invalid' : ''}`}
                    />
                    {errors.endDate ? <small className="p-error">{errors.endDate}</small> : null}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="okr-cycle-weeks" className="font-medium">Número de semanas</label>
                    <InputNumber
                        id="okr-cycle-weeks"
                        value={numberOfWeeks}
                        disabled={useCalendar}
                        onValueChange={(event) => updateField('numberOfWeeks', event.value)}
                        min={1}
                        useGrouping={false}
                        className={`w-full mt-2 ${errors.numberOfWeeks ? 'p-invalid' : ''}`}
                    />
                    {useCalendar ? <small>Se calcula con semanas de lunes a domingo, incluyendo períodos parciales.</small> : null}
                    {errors.numberOfWeeks ? <small className="p-error">{errors.numberOfWeeks}</small> : null}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="okr-cycle-points" className="font-medium">Puntos base</label>
                    <InputNumber
                        id="okr-cycle-points"
                        value={form.pointsBase}
                        onValueChange={(event) => updateField('pointsBase', event.value)}
                        min={1}
                        useGrouping={false}
                        className={`w-full mt-2 ${errors.pointsBase ? 'p-invalid' : ''}`}
                    />
                    {errors.pointsBase ? <small className="p-error">{errors.pointsBase}</small> : null}
                </div>

                <div className="field col-12">
                    <label htmlFor="okr-cycle-status" className="font-medium">Estado</label>
                    <Dropdown
                        id="okr-cycle-status"
                        value={form.status}
                        disabled={cycle?.status === 'closed'}
                        options={cycle?.status === 'closed' ? STATUS_OPTIONS : STATUS_OPTIONS.filter(option => option.value !== 'closed')}
                        onChange={(event) => updateField('status', event.value)}
                        className="w-full mt-2"
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default OkrCycleDialog;
